import { useDisclosure } from '@mantine/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOrganizationId } from '../../../helpers/auth-helpers/auth.hooks';
import {
  consumeRememberedThreadOnLoad,
  peekRememberedThreadOnLoad,
  rememberThread,
} from '../flora-aside.remembered-thread';
import { useFlora } from '../flora.context';
import { applyPageContextPreamble } from '../flora-page-context-preamble';
import { type OutgoingMessageContext, useFloraChatSession } from './use-flora-chat-session';

/** How long the history dropdown waits for its first payload before showing the empty state. */
const HISTORIES_LOAD_TIMEOUT_MS = 10_000;

interface UseFloraAsideChatOptions {
  /** Thread id to open; empty starts a new chat (or restores the remembered one in the right dock). */
  chatId?: string | null;
  /**
   * The page-view instance is tied to one published page: it always starts
   * fresh, never remembers its chat, and leaves "Analyze with Flora" questions
   * to the right dock.
   */
  isPageView?: boolean;
  /** Context prefixed to the first outgoing message; falls back to FloraContext's. */
  contextPreamble?: string;
}

/**
 * Owns a side-panel Flora conversation: the selected chat (with the right
 * dock's refresh restore), the shared chat session, the first-turn page
 * context, the context banner's expanded state, and "Analyze with Flora"
 * questions. Split from the panel's view so the right dock can keep one
 * conversation alive across page changes and panel close/reopen (see
 * `FloraAsideSessionProvider`), while the page viewer runs its own.
 *
 * @param {UseFloraAsideChatOptions} options - Which chat to open and how the panel is docked
 * @returns The session plus the panel's chat selection, history-loading and context-banner state
 *
 * @example
 * const chat = useFloraAsideChat({ isPageView: false });
 * chat.submitQuestion('How is velocity trending?');
 */
function useFloraAsideChat({ chatId, isPageView = false, contextPreamble }: UseFloraAsideChatOptions) {
  const organizationId = useOrganizationId();
  const [isContextExpanded, { toggle: toggleContext, close: collapseContext }] = useDisclosure(true);
  const {
    currentExternalQuestion,
    setCurrentExternalQuestion,
    contextualInfo,
    contextPreamble: contextPreambleFromContext,
  } = useFlora();
  const resolvedContextPreamble = contextPreamble ?? contextPreambleFromContext;

  // The chat this panel shows — the side panel's equivalent of the full page's
  // URL chat id. Only the right dock restores its last chat, and only on its
  // first mount after a page load (refresh recovery); after that the right
  // dock's session outlives page changes and panel close, so nothing needs
  // restoring. The page-view instance is tied to one published page and always
  // starts fresh. An external question ("Analyze with Flora") also starts
  // fresh — sending it into the restored chat would bury that chat's history
  // under the analysis.
  const [selectedChatId, setSelectedChatId] = useState<string | undefined>(() => {
    if (chatId) return chatId;
    if (isPageView || currentExternalQuestion) return undefined;
    return peekRememberedThreadOnLoad(organizationId);
  });
  // The restored id, pending a check that the chat still exists (it may have
  // been deleted elsewhere).
  const unverifiedRememberedChatRef = useRef<string | undefined>(chatId ? undefined : selectedChatId);
  // The remembered chat is keyed by organization, so a mount before the
  // organization resolves can't read it yet: restore it once it does. Until
  // the restored id lands in state, hold off persisting so the pending
  // `undefined` doesn't erase it.
  const restoreAwaitsOrganizationRef = useRef(!chatId && !isPageView && !currentExternalQuestion && !organizationId);
  const pendingRestoredChatIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!restoreAwaitsOrganizationRef.current || !organizationId) return;
    restoreAwaitsOrganizationRef.current = false;
    const rememberedChatId = peekRememberedThreadOnLoad(organizationId);
    if (rememberedChatId && !selectedChatId) {
      unverifiedRememberedChatRef.current = rememberedChatId;
      pendingRestoredChatIdRef.current = rememberedChatId;
      setSelectedChatId(rememberedChatId);
    }
  }, [organizationId, selectedChatId]);
  useEffect(() => {
    // Claim the restore only once it could have been read, so a mount with an
    // unresolved organization doesn't spend it.
    if (!isPageView && organizationId) {
      consumeRememberedThreadOnLoad();
    }
  }, [isPageView, organizationId]);
  useEffect(() => {
    if (chatId) {
      setSelectedChatId(chatId);
    }
  }, [chatId]);
  useEffect(() => {
    // Skip while the organization is momentarily unresolved rather than
    // erasing the remembered chat.
    if (isPageView || !organizationId) return;
    const pendingRestoredChatId = pendingRestoredChatIdRef.current;
    if (pendingRestoredChatId) {
      if (selectedChatId !== pendingRestoredChatId) return;
      pendingRestoredChatIdRef.current = undefined;
    }
    rememberThread(organizationId, selectedChatId);
  }, [isPageView, organizationId, selectedChatId]);

  const handleNewChatSelected = useCallback(() => {
    // A New Chat outranks a restore still waiting on the organization.
    restoreAwaitsOrganizationRef.current = false;
    pendingRestoredChatIdRef.current = undefined;
    setSelectedChatId(undefined);
    setCurrentExternalQuestion(null);
  }, [setCurrentExternalQuestion]);

  // Seed page/surface context on the first turn of a fresh thread only. The
  // thread carries context server-side afterwards.
  const prepareOutgoingMessage = useCallback(
    (message: string, { isFreshThread }: OutgoingMessageContext) =>
      resolvedContextPreamble && isFreshThread ? applyPageContextPreamble(resolvedContextPreamble, message) : message,
    [resolvedContextPreamble],
  );

  const session = useFloraChatSession({
    chatId: selectedChatId,
    contextualInfo,
    isSidebar: true,
    detachOnUnmount: true,
    onNewChat: handleNewChatSelected,
    onConversationStarted: setSelectedChatId,
    prepareOutgoingMessage,
  });
  const {
    questions,
    threadId,
    chatHistories,
    isActiveQuestion,
    isWaitingForFirstResponse,
    attachments,
    submitQuestion: submitSessionQuestion,
    sendTurn,
    handleNewChat,
  } = session;

  // When the first histories payload arrives, drop a remembered chat that no
  // longer exists (deleted elsewhere) rather than binding the panel to a hidden
  // thread that the next message would silently go to.
  const initialChatHistoriesRef = useRef(chatHistories);
  useEffect(() => {
    const rememberedChatId = unverifiedRememberedChatRef.current;
    if (!rememberedChatId || chatHistories === initialChatHistoriesRef.current) return;
    unverifiedRememberedChatRef.current = undefined;
    if (!chatHistories.some((history) => history.id === rememberedChatId)) {
      handleNewChat();
    }
  }, [chatHistories, handleNewChat]);
  // The session fetches histories on mount; until that first payload lands
  // the dropdown shows a spinner instead of a false "No conversations yet".
  // The fetch reports no failure, so give up after a while rather than spin
  // forever.
  const [hasHistoriesPayload, setHasHistoriesPayload] = useState(false);
  const [hasHistoriesLoadTimedOut, setHasHistoriesLoadTimedOut] = useState(false);
  useEffect(() => {
    if (chatHistories !== initialChatHistoriesRef.current) setHasHistoriesPayload(true);
  }, [chatHistories]);
  useEffect(() => {
    const timeoutId = setTimeout(() => setHasHistoriesLoadTimedOut(true), HISTORIES_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, []);
  const isLoadingHistories = !hasHistoriesPayload && !hasHistoriesLoadTimedOut;

  const submitQuestion = useCallback(
    (formattedMessage?: string, displayMessage?: string) => {
      if (attachments.hasUploading) return;
      const cleanup = submitSessionQuestion(formattedMessage, displayMessage);
      collapseContext();
      return cleanup;
    },
    [attachments.hasUploading, submitSessionQuestion, collapseContext],
  );

  // External questions (e.g. data analysis) are sent as-is, with their own
  // framing, and shown as "Analyzing your data...". Each one is taken from
  // FloraContext once — keyed by value, so React.StrictMode's re-run of the
  // effect can't send it twice, yet a later request (even with the same text)
  // is sent too — and always in a fresh chat: a conversation already open is
  // left (detached, not cancelled) first. The send waits for that reset and
  // for any attachment upload, so the question isn't consumed and then dropped.
  const consumedExternalQuestionRef = useRef<string | null>(null);
  const queuedExternalQuestionRef = useRef<string | null>(null);
  const hasOpenConversation =
    Boolean(threadId) || questions.length > 0 || isActiveQuestion || isWaitingForFirstResponse;
  useEffect(() => {
    if (isPageView) return;
    if (!currentExternalQuestion) {
      consumedExternalQuestionRef.current = null;
      return;
    }
    // Left in FloraContext while a file uploads, so nothing can drop it.
    if (attachments.hasUploading || consumedExternalQuestionRef.current === currentExternalQuestion) return;
    consumedExternalQuestionRef.current = currentExternalQuestion;
    queuedExternalQuestionRef.current = currentExternalQuestion;
    setCurrentExternalQuestion(null);
    if (hasOpenConversation) handleNewChat();
  }, [
    isPageView,
    currentExternalQuestion,
    attachments.hasUploading,
    setCurrentExternalQuestion,
    hasOpenConversation,
    handleNewChat,
  ]);
  useEffect(() => {
    const queuedQuestion = queuedExternalQuestionRef.current;
    if (!queuedQuestion || hasOpenConversation || attachments.hasUploading) return;
    queuedExternalQuestionRef.current = null;
    sendTurn(queuedQuestion, 'Analyzing your data...');
    collapseContext();
  }, [hasOpenConversation, attachments.hasUploading, sendTurn, collapseContext]);

  const selectedChatHistory = useMemo(
    () => chatHistories.find((history) => history.id === threadId),
    [chatHistories, threadId],
  );

  return {
    ...session,
    submitQuestion,
    selectedChatId,
    setSelectedChatId,
    selectedChatHistory,
    isLoadingHistories,
    isContextExpanded,
    toggleContext,
    collapseContext,
  };
}

type FloraAsideChat = ReturnType<typeof useFloraAsideChat>;

export { useFloraAsideChat };
export type { FloraAsideChat, UseFloraAsideChatOptions };
